import { hasValue } from 'swipeclock-frontend';
import {
  OFFLINE_PUNCH_ACTIVE_STATUSES,
  OFFLINE_PUNCH_RETRYABLE_STATUSES,
} from '../constants/offlinePunchQueueStatuses';
import { generateId } from './generateId';
import { getExpiresAt, isQueueItemExpired } from './offlinePunchRetrySchedule';
import { pickAllowedQueueFields } from './offlineStorageAllowList';

export const getOfflinePunchErrorMessage = (error) => {
  const status = error?.status ?? error?.originalStatus;

  if (status === 401 || status === 403) {
    return 'Your session has expired. Please log in again.';
  }

  return (
    error?.data?.errorMessage ||
    error?.data?.message ||
    error?.message ||
    'Unknown error'
  );
};

export const isReadyForRetry = (item, now) =>
  OFFLINE_PUNCH_RETRYABLE_STATUSES.has(item.status) &&
  item.awaitingAuth !== true &&
  (!hasValue(item.retryAfter) || item.retryAfter <= now);

export const isWaitingForRetry = (item, now) =>
  OFFLINE_PUNCH_RETRYABLE_STATUSES.has(item.status) &&
  hasValue(item.retryAfter) &&
  item.retryAfter > now;

export const getEarliestRetryDelayMs = (queue, now) => {
  const waitingItems = queue.filter((item) => isWaitingForRetry(item, now));

  if (waitingItems.length === 0) {
    return null;
  }

  const earliestRetry = Math.min(
    ...waitingItems.map((item) => item.retryAfter)
  );

  return Math.max(0, earliestRetry - now);
};

export const countPendingItems = (queue) =>
  queue.filter((item) => OFFLINE_PUNCH_ACTIVE_STATUSES.has(item.status)).length;

export const purgeExpiredItems = (queue, now = Date.now()) =>
  queue.filter((item) => !isQueueItemExpired(item, now));

export const hasReadyQueueItem = (
  queue,
  now = Date.now(),
  { alreadyPurged = false } = {}
) => {
  const items = alreadyPurged ? queue : purgeExpiredItems(queue, now);
  return items.some((item) => isReadyForRetry(item, now));
};

export const buildQueueItem = (payload, isMobileMode, now = new Date()) => {
  const punchDateTime = now.toISOString();
  const { punchSessionId, id: payloadId } = payload;
  const allowedFields = pickAllowedQueueFields(payload);

  return {
    queuedAt: punchDateTime,
    punchDateTime,
    expiresAt: getExpiresAt(punchDateTime),
    status: 'pending',
    retryCount: 0,
    mobile: isMobileMode,
    punchSessionId,
    ...allowedFields,
    id: punchSessionId ?? payloadId ?? allowedFields.id ?? generateId(),
  };
};
